/**
 * Main RBAC hook — use this in components to check permissions.
 *
 * Usage:
 *   const { canGlobal, canStage, canSource, hasRouteKey } = usePermissions();
 *   if (canGlobal("lead_management", "edit")) { ... }
 *
 * Custom field **values** on leads (per role `global.custom_field_{id}`):
 *   canGlobal(`custom_field_${customInputId}`, "view")
 *   canGlobal(`custom_field_${customInputId}`, "edit")
 * Requires the `leads` route key (same idea as lead data). Values are stored as
 * `view` / `edit` control types; legacy `{ access }` is still read if present.
 *
 * Custom field **definitions** (admin / Settings):
 *   canGlobal("custom_management", "view" | "create" | "edit" | "delete")
 */

import { useMemo } from "react";
import { useAppSelector } from "@/stores/hook";
import {
  selectAccessConfig,
  selectCanBypassRbacChecks,
} from "@/rbac/rbacSlice";
import {
  controlGrantsAction,
  normalizeControlValue,
  type AccessConfig,
} from "@/rbac/utils";
import { ROUTE_KEY_LEADS } from "@/rbac/normalizeAccessConfig";

// ─── Helpers ─────────────────────────────────────────────────────

function isGranted(raw: any, action: string): boolean {
  return controlGrantsAction(normalizeControlValue(raw), action);
}

function getActionValue(mod: any, action: string): any {
  if (!mod) return undefined;
  return mod[action];
}

/**
 * Fallback for `canGlobal("lead_management", action)` when the new wizard owns
 * permissions and `global.lead_management` no longer exists. Derives the answer
 * from the highest `lead_management.level` across stages. Action thresholds
 * mirror the backend `rankControl` ladder in `role.adapters.ts`.
 */
const LEAD_LEVEL_RANK: Record<string, number> = {
  admin: 6,
  manage: 5,
  allow: 5,
  edit: 4,
  view: 3,
  own: 3,
  deny: 1,
};

const LEAD_ACTION_THRESHOLD: Record<string, number> = {
  view: 3,
  view_deadline: 3,
  view_activity: 3,
  edit: 4,
  // `create` sits at the same rank as `edit` because the redesigned wizard
  // exposes rows (Deadlines, Follow-ups) where `edit` is the strongest
  // available level — the user expects "edit" to include creating records.
  create: 4,
  export: 5,
  set_deadline: 5,
  delete: 6,
};

function stageDerivedLeadAction(
  stages: AccessConfig["stages"] | undefined,
  action: string
): boolean {
  if (!Array.isArray(stages) || stages.length === 0) return false;
  const threshold = LEAD_ACTION_THRESHOLD[action] ?? 3;
  for (const st of stages) {
    const lm = (st as any)?.permissions?.lead_management;
    const level =
      lm && typeof lm === "object" && typeof lm.level === "string"
        ? (lm.level as string)
        : undefined;
    if (!level) continue;
    if ((LEAD_LEVEL_RANK[level] ?? 0) >= threshold) return true;
  }
  return false;
}

/**
 * Raw control for `global.custom_field_{id}` — supports `view` / `edit` and legacy `access`.
 */
function getCustomFieldValueControl(mod: any, action: "view" | "edit"): any {
  if (!mod) return undefined;
  const direct = mod[action];
  if (direct !== undefined) return direct;
  if (mod.access !== undefined) {
    return mod.access === "allow" ? "allow" : "deny";
  }
  return undefined;
}

/**
 * Per-stage `lead_followup` / `lead_deadline` permissions are stored as
 * `{ level: "edit" | "view" | "deny" }` (redesign §10). These helpers compare
 * the stored level against a required level using the standard rank ladder.
 *
 * STRICT per-stage: there is no fallback to legacy `global.lead_management`
 * keys (`view_deadline` / `set_deadline`). The per-stage `lead_deadline.level`
 * is the only source of truth.
 */
const LEVEL_THRESHOLD: Record<"view" | "edit", number> = {
  view: 3,
  edit: 4,
};

function readLevelValue(mod: unknown): string | undefined {
  if (typeof mod === "string") return mod;
  if (mod && typeof mod === "object" && typeof (mod as any).level === "string") {
    return (mod as any).level as string;
  }
  return undefined;
}

function readStageLevel(
  stages: AccessConfig["stages"] | undefined,
  stageId: number,
  moduleKey: "lead_followup" | "lead_deadline",
  global?: Record<string, unknown> | null
): string | undefined {
  if (!Array.isArray(stages)) return undefined;
  const sid = Number(stageId);
  const entry = stages.find(
    (s) => s != null && Number((s as { stageId?: unknown }).stageId) === sid
  );
  if (!entry) return undefined;
  const stageLevel = readLevelValue(entry?.permissions?.[moduleKey]);
  if (stageLevel) return stageLevel;
  if (moduleKey === "lead_followup") {
    // Mirror the server (`followUpLevelForStageEntry`): a stage entry with
    // no follow-up dial falls back to the role-wide `global.lead_followup`,
    // else "edit". Hiding the chip here while the server would accept the
    // write made users "unable to update" a follow-up they were allowed to.
    return readLevelValue((global as any)?.lead_followup) ?? "edit";
  }
  return undefined;
}

function levelGrants(
  level: string | undefined,
  required: "view" | "edit"
): boolean {
  if (!level) return false;
  return (LEAD_LEVEL_RANK[level] ?? 0) >= LEVEL_THRESHOLD[required];
}

// ─── Hook ────────────────────────────────────────────────────────

export interface UsePermissionsResult {
  accessConfig: AccessConfig | null;
  bypassesRbacChecks: boolean;
  canGlobal: (moduleKey: string, action: string) => boolean;
  canStage: (
    stageId: number,
    moduleKey: string,
    action: string,
    trackId?: number,
    subTrackId?: number
  ) => boolean;
  canSource: (sourceId: number, moduleKey: string, action: string) => boolean;
  /**
   * Per-stage follow-up access. Reads `stages[stageId].permissions.lead_followup.level`.
   * `view` requires level >= "view"; `edit` requires level >= "edit" (lets users
   * create / complete / delete follow-ups). Admin bypass and missing-stage
   * fallthrough mirror `canStage`.
   */
  canFollowup: (stageId: number, level?: "view" | "edit") => boolean;
  /**
   * Per-stage deadline access. Reads `stages[stageId].permissions.lead_deadline.level`.
   * Strictly per-stage — does NOT fall back to legacy
   * `global.lead_management.{view_deadline, set_deadline}`. `edit` covers
   * setting / completing / removing deadlines.
   */
  canDeadline: (stageId: number, level?: "view" | "edit") => boolean;
  /**
   * Per-stage "Activity timeline" (`stages[stageId].permissions.lead_activity`).
   * False only when the stage is set to Deny — the server's
   * `LeadPolicy.activityTimelineLevelForStage` then serves an empty timeline,
   * so the drawer hides Activity Log instead. Call History is not affected.
   */
  canActivity: (stageId: number) => boolean;
  hasRouteKey: (routeKey: string) => boolean;
  hasRouteKeys: (keys: string[], match?: "any" | "all") => boolean;
  getRawGlobalModule: (moduleKey: string) => any;
  getRawStageModule: (stageId: number, moduleKey: string) => any;
  getRawSourceModule: (sourceId: number, moduleKey: string) => any;
}

export function usePermissions(): UsePermissionsResult {
  const accessConfig = useAppSelector(selectAccessConfig);
  const bypassesRbacChecks = useAppSelector(selectCanBypassRbacChecks);

  return useMemo(() => {
    const global = accessConfig?.global;
    const routeKeysList = accessConfig?.routeKeys;
    const hasLeadsRoute =
      bypassesRbacChecks ||
      (Array.isArray(routeKeysList) && routeKeysList.includes(ROUTE_KEY_LEADS));

    // ── Global scope ──

    const canGlobal = (moduleKey: string, action: string): boolean => {
      if (bypassesRbacChecks) return true;
      if (moduleKey === "lead_management" && !hasLeadsRoute) return false;
      // Value-level custom field permissions apply to lead/child record data — require Leads module.
      if (moduleKey.startsWith("custom_field_") && !hasLeadsRoute) return false;
      // New wizard: `global.lead_management` is dropped in favor of per-stage
      // `lead_management.level`. Derive the answer from stages so legacy
      // `<Can moduleKey="lead_management" ...>` callsites keep working.
      if (
        moduleKey === "lead_management" &&
        (!global || global.lead_management == null)
      ) {
        return stageDerivedLeadAction(accessConfig?.stages, action);
      }
      if (!global) return false;
      const mod = global[moduleKey];
      const raw =
        moduleKey.startsWith("custom_field_") &&
        (action === "view" || action === "edit")
          ? getCustomFieldValueControl(mod, action)
          : getActionValue(mod, action);
      // Lead management: viewing tabs/data vs editing — control type "view" must grant visibility
      // (isGranted compares action name "view_deadline" literally, which breaks read-only roles).
      if (moduleKey === "lead_management") {
        if (action === "view_deadline" || action === "view_activity") {
          const c = normalizeControlValue(raw);
          if (c === "deny") return false;
          return c === "allow" || c === "view" || c === "own";
        }
        if (action === "set_deadline") {
          const c = normalizeControlValue(raw);
          return c === "allow" || c === "own";
        }
        if (action === "view_archived_history") {
          // Strict allow-only. Mirrors LeadPolicy.canViewArchivedHistory on
          // the server: even admins are bound by the flag (preset admins
          // ship with it set to allow so this is invisible in practice).
          return normalizeControlValue(raw) === "allow";
        }
      }
      return isGranted(raw, action);
    };

    // ── Stage scope ──

    const canStage = (
      stageId: number,
      moduleKey: string,
      action: string,
      trackId?: number,
      subTrackId?: number
    ): boolean => {
      if (bypassesRbacChecks) return true;
      if (!hasLeadsRoute) return false;

      const stages = accessConfig?.stages;
      if (!Array.isArray(stages)) return false;

      const sid = Number(stageId);
      const entry = stages.find(
        (s) => s != null && Number((s as { stageId?: unknown }).stageId) === sid
      );
      const mod = entry?.permissions?.[moduleKey] as any;

      // Track-level override for track_management view/edit
      if (
        moduleKey === "track_management" &&
        trackId != null &&
        (action === "view" || action === "edit")
      ) {
        const tr = mod?.tracks?.[String(trackId)];
        if (subTrackId != null) {
          const perSub = tr?.subTracks?.[String(subTrackId)]?.[action];
          if (perSub != null) return isGranted(perSub, action);
        }
        const perTrack = tr?.[action];
        return isGranted(
          perTrack != null ? perTrack : getActionValue(mod, action),
          action
        );
      }

      if (
        moduleKey === "stage_management" &&
        (action === "filter_view" || action === "filter_edit")
      ) {
        // `lead_management.level` is the source of truth for `filter_view`:
        // any non-deny level means the role can read leads in this stage,
        // so the stage must appear in filter dropdowns and dashboard
        // breakdowns. A stored `filter_view: "deny"` no longer overrides
        // — the wizard used to auto-write it when the "Show in filters"
        // toggle defaulted off, which silently hid stages from counsellors
        // with explicit view access. `filter_edit` (changing saved-view
        // config) intentionally stays strict.
        if (action === "filter_view") {
          const leadMgmt = (entry?.permissions as any)?.lead_management;
          const level = leadMgmt?.level;
          if (typeof level === "string" && level !== "deny") return true;
        }
        const raw = getActionValue(mod, action);
        if (raw === "allow") return true;
        if (raw === "deny") return false;
        if (getActionValue(mod, "view_edit") === "allow") return true;
        return false;
      }

      return isGranted(getActionValue(mod, action), action);
    };

    // ── Source scope ──

    const canSource = (
      sourceId: number,
      moduleKey: string,
      action: string
    ): boolean => {
      if (bypassesRbacChecks) return true;
      if (!hasLeadsRoute) return false;
      const sources = accessConfig?.sources;
      if (!Array.isArray(sources)) return false;
      const entry = sources.find((s) => s?.sourceId === sourceId);
      return isGranted(
        getActionValue(entry?.permissions?.[moduleKey], action),
        action
      );
    };

    // ── Per-stage Follow-up / Deadline ──

    const canFollowup = (
      stageId: number,
      level: "view" | "edit" = "view"
    ): boolean => {
      if (bypassesRbacChecks) return true;
      if (!hasLeadsRoute) return false;
      // Caller may not yet know the stage (e.g. activeData still loading).
      // Fall through to permissive so transient `NaN` doesn't strobe the UI;
      // the actual stage-bound check kicks in once data arrives.
      if (!Number.isFinite(Number(stageId))) return true;
      return levelGrants(
        readStageLevel(
          accessConfig?.stages,
          Number(stageId),
          "lead_followup",
          global as Record<string, unknown> | null | undefined
        ),
        level
      );
    };

    const canDeadline = (
      stageId: number,
      level: "view" | "edit" = "view"
    ): boolean => {
      if (bypassesRbacChecks) return true;
      if (!hasLeadsRoute) return false;
      if (!Number.isFinite(Number(stageId))) return true;
      return levelGrants(
        readStageLevel(accessConfig?.stages, Number(stageId), "lead_deadline"),
        level
      );
    };

    const canActivity = (stageId: number): boolean => {
      if (bypassesRbacChecks) return true;
      if (!hasLeadsRoute) return false;
      if (!Number.isFinite(Number(stageId))) return true;
      if (!Array.isArray(accessConfig?.stages)) return true;
      const sid = Number(stageId);
      const entry = accessConfig!.stages.find(
        (s) => s != null && Number((s as { stageId?: unknown }).stageId) === sid
      );
      return readLevelValue(entry?.permissions?.lead_activity) !== "deny";
    };

    // ── Route keys ──

    const hasRouteKey = (routeKey: string): boolean => {
      if (bypassesRbacChecks) return true;
      if (!routeKey || !Array.isArray(routeKeysList)) return false;
      return routeKeysList.includes(routeKey);
    };

    const hasRouteKeys = (
      keys: string[],
      match: "any" | "all" = "any"
    ): boolean => {
      if (bypassesRbacChecks) return true;
      if (!Array.isArray(keys) || keys.length === 0) return false;
      return match === "all" ? keys.every(hasRouteKey) : keys.some(hasRouteKey);
    };

    // ── Raw access (for role form editors) ──

    const getRawGlobalModule = (moduleKey: string) => global?.[moduleKey];

    const getRawStageModule = (stageId: number, moduleKey: string) => {
      if (!Array.isArray(accessConfig?.stages)) return undefined;
      const sid = Number(stageId);
      return accessConfig!.stages.find(
        (s) => s != null && Number((s as { stageId?: unknown }).stageId) === sid
      )?.permissions?.[moduleKey];
    };

    const getRawSourceModule = (sourceId: number, moduleKey: string) => {
      if (!Array.isArray(accessConfig?.sources)) return undefined;
      const srid = Number(sourceId);
      return accessConfig!.sources.find(
        (s) => s != null && Number((s as { sourceId?: unknown }).sourceId) === srid
      )?.permissions?.[moduleKey];
    };

    return {
      accessConfig,
      bypassesRbacChecks,
      canGlobal,
      canStage,
      canSource,
      canFollowup,
      canDeadline,
      canActivity,
      hasRouteKey,
      hasRouteKeys,
      getRawGlobalModule,
      getRawStageModule,
      getRawSourceModule,
    };
  }, [accessConfig, bypassesRbacChecks]);
}
